import { useEffect, useState } from 'react';

const KALTURA_SCRIPTS = {
  KalturaPlayer:
    'https://cdnapisec.kaltura.com/p/966871/embedPlaykitJs/uiconf_id/52245692?updated=1721915821',
  kWidget:
    'https://cdnapisec.kaltura.com/p/966871/sp/96687100/embedIframeJs/uiconf_id/46364641/partner_id/966871?updated=1721915821'
};

export type KalturaGlobal = keyof typeof KALTURA_SCRIPTS;

const pendingScripts = new Map<KalturaGlobal, Promise<void>>();

export function loadKalturaScript(global: KalturaGlobal) {
  let promise = pendingScripts.get(global);

  if (!promise) {
    promise = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = KALTURA_SCRIPTS[global];
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        // Forget the failure so the next player mount can retry
        pendingScripts.delete(global);
        script.remove();
        reject(new Error(`Failed to load Kaltura ${global}`));
      };
      document.head.appendChild(script);
    });
    pendingScripts.set(global, promise);
  }

  return promise;
}

/**
 * Loads a Kaltura player script the first time a video needs it, instead of
 * on every page, and reports when its window global is ready to use.
 */
export function useKalturaScript(global: KalturaGlobal = 'KalturaPlayer') {
  const [isLoaded, setIsLoaded] = useState(() => global in window);

  useEffect(() => {
    if (isLoaded) {
      return;
    }

    let isActive = true;
    loadKalturaScript(global).then(
      () => isActive && setIsLoaded(true),
      () => {}
    );

    return () => {
      isActive = false;
    };
  }, [global, isLoaded]);

  return isLoaded;
}

export default useKalturaScript;
